/** Team-wide positioning and assignments. Source: teamAIcontroller.cpp:36-1018. */

import { Vector3 } from 'three';
import { PITCH, TICK_MS } from '../core/constants';
import { clamp, curve, normalizedClamp, normalizedOr } from '../core/math';
import { ROLE_OFFENSIVENESS, type FormationEntry, type PlayerRole } from './formation';
import type { Match, MatchPlayer, TeamState } from './match';
import type { MentalImage } from './mentalimage';

const FORWARD = new Vector3(0, -1, 0);

interface Tactics {
  offenseDepth: number;
  defenseDepth: number;
  offenseWidth: number;
  defenseWidth: number;
  offenseOwnHalf: number;
  defenseOwnHalf: number;
  offenseMidfield: number;
  defenseMidfield: number;
  offenseMidfieldStrength: number;
  defenseMidfieldStrength: number;
  offenseSideStrength: number;
  defenseSideStrength: number;
  offenseMicroStrength: number;
  defenseMicroStrength: number;
}

const BASE: Tactics = {
  offenseDepth: 0.9,
  defenseDepth: 0.75,
  // How much of the pitch's width a side stands across, before everything below narrows it
  // again. Raising these to 1.0 / 0.95 was tried and MEASURED OVER EIGHT WHOLE MATCHES: it
  // put more goals in (2.75 a match) but cut throw-ins from 1.2 to 0.3 and pushed the share
  // of shots scored to 19% against 11% in real football, because stretching a side sideways
  // also stretches its defence and opens the middle. Reverted; the narrowness that keeps the
  // ball off the touchlines is not in these two numbers.
  offenseWidth: 0.9,
  defenseWidth: 0.8,
  offenseOwnHalf: 0.52,
  defenseOwnHalf: 0.54,
  offenseMidfield: 0.6,
  defenseMidfield: 0.5,
  offenseMidfieldStrength: 0.35,
  defenseMidfieldStrength: 0.35,
  offenseSideStrength: 0.1,
  defenseSideStrength: 0.4,
  offenseMicroStrength: 0.7,
  defenseMicroStrength: 0.8,
};

/**
 * How willing a side is to commit forward, from 0 to 1, `teamAIcontroller.cpp:963-993`.
 *
 * Two halves, weighted equally. One is the state of the game: a side that is behind wants
 * goals and a side that is ahead wants the clock to run out, and both of those wants grow as
 * the match goes on — at kick-off the scoreline barely moves the shape, by the closing
 * minutes it moves it twice as much. The other half is simply who has had the ball lately.
 *
 * The scoreline term saturates: two goals either way is as desperate, or as content, as a
 * side ever gets.
 */
export function offensivenessBias(match: Match, team: TeamState): number {
  const own = team.score;
  const opponent = match.opponentTeam(team.id).score;
  const goalFactor = clamp(0.5 + (opponent - own) * 0.25, 0, 1);
  // 6,300,000 ms is the end of the first period of extra time on the football clock.
  const timeFactor = 0.5 + 0.5 * clamp(match.matchTimeMs / 6_300_000, 0, 1);
  const fromTheGame = clamp(0.5 + (goalFactor - 0.5) * timeFactor, 0, 1);
  const fromPossession = 1 - Math.abs(match.recentPossessionFactor - team.id);
  return fromTheGame * 0.5 + fromPossession * 0.5;
}

/**
 * How much of the match's possession-side history counts, and when it was last thrown away.
 *
 * The original wipes that history at every restart: `Match::ResetSituation` calls
 * `possessionSideHistory->Clear()` (`match.cpp:668-669`), and every throw-in, corner, goal
 * kick, free kick, penalty, kick-off and goal goes through it. Without the wipe both blocks
 * keep leaning towards wherever the passage of play that has just been stopped happened to be,
 * for the whole six seconds the window looks back over.
 *
 * The buffer belongs to the match and cannot be emptied from here, but narrowing the window
 * gives the identical answer. `Match.averagePossessionSide(d)` averages the last
 * `floor(d / TICK_MS) + 1` entries, so asking for exactly the entries recorded since the
 * restart reads precisely what a cleared buffer would hold — and `ValueHistory::GetAverage`
 * returns 0 while the buffer is empty (`utils.hpp:89-103`), which is the no-entries case here.
 */
interface PossessionSideWindow {
  resetSerial: number;
  samples: number;
}

/** Per-match, because two matches must not share one window. */
const possessionSideWindows = new WeakMap<Match, PossessionSideWindow>();

function possessionSideWindow(match: Match): PossessionSideWindow {
  let recorded = possessionSideWindows.get(match);
  if (!recorded) {
    recorded = { resetSerial: match.resetSerial, samples: 0 };
    possessionSideWindows.set(match, recorded);
  }
  return recorded;
}

/** The original's `GetAveragePossessionSide(6000)`, restarts included. */
function averagePossessionSide(match: Match): number {
  const recorded = possessionSideWindow(match);
  if (recorded.samples === 0) return 0;
  // The buffer itself only keeps 600 entries, so past that the two windows are the same set.
  return match.averagePossessionSide(Math.min(recorded.samples - 1, 600) * TICK_MS);
}

/** Run once after possession has been resolved and before individual controllers. */
export function updateTeamAI(match: Match): void {
  const recorded = possessionSideWindow(match);
  if (recorded.resetSerial !== match.resetSerial) {
    recorded.resetSerial = match.resetSerial;
    recorded.samples = 0;
  }

  for (const team of match.teams) updateFadingPossession(match, team);
  if (match.inPlay && !match.inSetPiece) updateTeamShape(match);

  // `Match.step` records its possession-side sample immediately after this call, under the
  // original's own condition (`match.cpp:1020-1027`). Counting it here, as the last act, keeps
  // the count level with the buffer both during this call and for the rest of the tick — and it
  // has to sit outside the shape update, because a side positioning itself for a set piece is
  // still "in play" and still recorded.
  if (match.inPlay && match.bestPossessionTeamId >= 0) recorded.samples++;
}

function updateTeamShape(match: Match): void {
  const image = match.mentalImage(0);
  for (const team of match.teams) {
    team.offsideTrapX = calculateOffsideTrap(match, team, image);

    // teamAIcontroller.cpp:101 — the tactics are refreshed once a second, not every tick.
    if (match.timeMs - team.tacticsUpdatedAtMs >= 1000) {
      team.offensiveness = offensivenessBias(match, team);
      team.tacticsUpdatedAtMs = match.timeMs;
    }

    if (match.timeMs - team.dynamicRolesUpdatedAtMs >= 400) {
      calculateDynamicRoles(match, team);
      team.dynamicRolesUpdatedAtMs = match.timeMs;
    }
    if (match.timeMs - team.manMarkingUpdatedAtMs >= 400) {
      calculateManMarking(match, team, image);
      team.manMarkingUpdatedAtMs = match.timeMs;
    }
    if (match.timeMs - team.attackingRunCheckedAtMs >= 500) {
      updateAttackingRun(match, team, image);
      team.attackingRunCheckedAtMs = match.timeMs;
    }
    if (match.timeMs - team.forwardSupportUpdatedAtMs >= 1500) {
      updateForwardSupport(team);
      team.forwardSupportUpdatedAtMs = match.timeMs;
    }
  }
}

function updateFadingPossession(match: Match, team: TeamState): void {
  if (match.ballRetainer || !match.inPlay || match.inSetPiece) {
    const owner = match.ballRetainer?.teamId ?? match.bestPossessionTeamId;
    team.fadingPossessionAmount = owner === team.id ? 1.5 : 0.5;
    return;
  }
  const tmp =
    team.fadingPossessionAmount * 0.995 + clamp(team.possessionAmount, 0.5, 1.5) * 0.005;
  team.fadingPossessionAmount += clamp(tmp - team.fadingPossessionAmount, -0.005, 0.005);
}

/** The position a role wants in the live, ball-shaped formation. */
export function adaptedFormationPosition(
  match: Match,
  player: MatchPlayer,
  useDynamic: boolean,
): Vector3 {
  const team = match.team(player.teamId);
  const role = useDynamic ? player.dynamicRole : player.role;
  const t = mixedTactics(role);
  const mindset = ROLE_OFFENSIVENESS[role];
  let focal = match.designatedPossessionPlayer.position.clone();
  const urgency = 1 - normalizedClamp(focal.distanceTo(player.position), 2, 30);
  const ballX = match.ball.averagePosition(3500 * (1 - urgency * 0.7), _avgA).x;
  const ballY = match.ball.averagePosition(4000 * (1 - urgency * 0.5), _avgB).y;
  const offensiveness = team.offensiveness;

  let offenseSide = t.offenseSideStrength + (-0.5 + mindset) * 0.2;
  let defenseSide = t.defenseSideStrength + (0.5 - mindset) * 0.2;
  offenseSide = clamp(offenseSide - 0.3 + offensiveness * 0.3, 0, 1);
  defenseSide = clamp(defenseSide - 0.3 + (1 - offensiveness) * 0.3, 0, 1);

  const possessionAmountBias = normalizedClamp(team.fadingPossessionAmount - 0.5, 0.3, 0.7);
  const ballBias = normalizedClamp((ballX / PITCH.halfLength) * -team.side, -0.7, 0.7);
  const ballBiasBias = (1 - Math.abs(possessionAmountBias * 2 - 1)) * 0.6;
  let possessionBias =
    possessionAmountBias * (1 - ballBiasBias) + ballBias * ballBiasBias;
  possessionBias = clamp(possessionBias + (offensiveness - 0.5) * 0.3, 0, 1);

  focal = match.ball
    .averagePosition(3000, _avgA)
    .clone()
    .multiplyScalar(possessionBias)
    .add(
      match.ball
        .averagePosition(2000, _avgB)
        .clone()
        .multiplyScalar(0.5)
        .addScaledVector(focal, 0.5)
        .multiplyScalar(1 - possessionBias),
    );
  focal.z = 0;

  const depth = 0.45 * (t.offenseDepth * possessionBias + t.defenseDepth * (1 - possessionBias));
  const width = 0.95 * (t.offenseWidth * possessionBias + t.defenseWidth * (1 - possessionBias));
  const offsetX =
    PITCH.halfLength *
    team.side *
    ((t.offenseOwnHalf * 2 - 1) * possessionBias +
      (t.defenseOwnHalf * 2 - 1) * (1 - possessionBias));
  const sideStrength = offenseSide * possessionBias + defenseSide * (1 - possessionBias);
  const sideFocus = possessionBias * 2 - 1;
  const sideX =
    0.2 * sideFocus * -team.side * PITCH.halfLength -
    0.8 * averagePossessionSide(match) * PITCH.halfLength;
  let centerX = clamp(ballX * (1 - sideStrength) + sideX * sideStrength + offsetX, -55, 55);
  let centerY = clamp(ballY, -36, 36);
  centerX *= (1 - depth) * 0.95 + 0.05;
  centerY *= (1 - width) * 0.9 + 0.1;

  let backX = centerX - depth * PITCH.halfLength * -team.side;
  const frontX = centerX + depth * PITCH.halfLength * -team.side;
  const lowY = centerY - width * PITCH.halfWidth;
  const highY = centerY + width * PITCH.halfWidth;
  if (backX * team.side > team.offsideTrapX * team.side) backX = team.offsideTrapX;

  const defensiveFocus = new Vector3(
    clamp(focal.x + team.side * 2, -PITCH.halfLength, backX * team.side),
    focal.y * 0.9,
    0,
  );
  const microFocus = new Vector3(
    clamp(focal.x - team.side, -PITCH.halfLength, PITCH.halfLength),
    focal.y * 0.9,
    0,
  )
    .multiplyScalar(possessionBias)
    .addScaledVector(defensiveFocus, 1 - possessionBias);
  let microStrength =
    t.offenseMicroStrength * possessionBias + t.defenseMicroStrength * (1 - possessionBias);
  const microSide =
    normalizedClamp((ballX / PITCH.halfLength) * -team.side, -0.7, 0.7) * 0.7 + 0.3;
  const autoMicro =
    Math.pow(microSide, 0.8) * possessionBias +
    Math.pow(1 - microSide, 0.6) * (1 - possessionBias);
  microStrength *= 0.2 + 0.8 * autoMicro;

  const manualMidfield =
    t.offenseMidfield * possessionBias + t.defenseMidfield * (1 - possessionBias);
  const autoMidfield = normalizedClamp(
    (ballX / PITCH.halfLength) * -team.side,
    -0.8,
    0.8,
  );
  const midfieldFocus = manualMidfield * 0.7 + autoMidfield * 0.3;
  const midfieldStrength =
    t.offenseMidfieldStrength * possessionBias +
    t.defenseMidfieldStrength * (1 - possessionBias);
  const yStrength = 0.5 * possessionBias + 0.2 * (1 - possessionBias);

  return mapFormation(
    useDynamic ? player.dynamicFormationEntry : player.formationEntry,
    team.side,
    backX,
    frontX,
    lowY,
    highY,
    ballY,
    yStrength,
    microFocus,
    microStrength,
    midfieldFocus,
    midfieldStrength,
  );
}

function mapFormation(
  entry: FormationEntry,
  side: -1 | 1,
  backX: number,
  frontX: number,
  lowY: number,
  highY: number,
  yFocus: number,
  yFocusStrength: number,
  microFocus: Vector3,
  microFocusStrength: number,
  midfieldFocus: number,
  midfieldFocusStrength: number,
): Vector3 {
  const position = entry.position.clone();
  const stretch =
    curve(clamp(1 - Math.abs(position.x * 1.2), 0, 1), 1) * midfieldFocusStrength;
  position.x = position.x * (1 - stretch) + (midfieldFocus * 2 - 1) * stretch;
  position.x = backX + (position.x * 0.5 + 0.5) * (frontX - backX);
  position.y = lowY + (position.y * -side * 0.5 + 0.5) * (highY - lowY);
  const pure = position.clone();

  const yDistance = clamp(Math.abs(yFocus - position.y) / Math.abs(highY - lowY), 0, 1);
  const yBias =
    (1 - yDistance) * (0.2 + (0.8 * Math.abs(yFocus)) / PITCH.halfWidth) * yFocusStrength;
  position.y = position.y * (1 - yBias) + yFocus * yBias;

  const dist = microFocus.clone().sub(pure).multiply(new Vector3(1, 0.8, 0)).length() / 50;
  if (dist < 1) {
    let bias = curve(1 - dist, 0.3);
    bias += (1 - normalizedClamp(Math.abs(dist - 0.15), 0, 0.25)) * 0.1;
    bias = clamp(bias, 0, 1) * microFocusStrength;
    // The pull towards the action moves a player UP AND DOWN the pitch and leaves where he
    // stands ACROSS it alone.
    //
    // DIVERGENCE, AND WHAT IT IS WORTH. The original drags him sideways as well, six parts
    // the ball's position to four parts his own (`AIfunctions.cpp:126-133`; its own knob for
    // this, `homogeneousYPositionBias`, is set to 0.4 and its comment says 1.0 means "don't
    // influence player's resulting Y position"). Since the ball is usually near the middle,
    // that pulls the whole side into the middle: measured over four whole matches, the
    // WIDEST man of a side stood 20-22 m from the centre line and reached the last four
    // metres before a touchline on one tick in a thousand. Nobody being near a touchline is
    // why the ball never crosses one — 1.1% of all passes and shots struck would have left
    // the field sideways, and the game managed one throw-in a match against about forty in
    // real football. A real side keeps its width and slides up and down; it does not
    // collapse into a huddle around the ball.
    const focusPosition = new Vector3(microFocus.x, position.y, 0);
    position.multiplyScalar(1 - bias).addScaledVector(focusPosition, bias);
  }
  position.x = clamp(position.x, -PITCH.halfLength, PITCH.halfLength);
  position.y = clamp(position.y, -PITCH.halfWidth, PITCH.halfWidth);
  return position;
}

function mixedTactics(role: PlayerRole): Tactics {
  const t = { ...BASE };
  if (role === 'CB') t.offenseWidth += 0.2;
  if (role === 'LB' || role === 'RB') {
    t.defenseOwnHalf -= 0.075;
    t.offenseWidth += 0.2;
    t.offenseOwnHalf -= 0.1;
  }
  if (role === 'LM' || role === 'RM') {
    t.defenseOwnHalf -= 0.05;
    t.offenseOwnHalf -= 0.1;
  }
  if (role === 'AM' || role === 'CF') t.defenseDepth += 0.125;
  for (const key of Object.keys(t) as (keyof Tactics)[]) t[key] = clamp(t[key], 0, 1);
  return t;
}

function calculateOffsideTrap(match: Match, team: TeamState, image: MentalImage): number {
  const offensiveness = team.offensiveness;
  const startDistance = 30 + 20 * offensiveness;
  const forceDistance = 6;
  let deepest = (PITCH.halfLength - startDistance) * team.side;
  let ballX = match.ball.predict(0, _probeA).x * team.side;
  const offsetX = 20 + 10 * (1 - offensiveness);
  const bias = normalizedClamp(
    ballX,
    PITCH.halfLength - startDistance - offsetX,
    PITCH.halfLength - forceDistance,
  );
  ballX = (ballX + offsetX * (1 - bias)) * team.side;
  if (ballX * team.side > deepest * team.side) deepest = ballX;
  const future = match.ball.predict(700, _probeA).x;
  if (future * team.side > deepest * team.side) deepest = future;
  const opponent = match.opponentTeam(team.id).designatedPlayer;
  const oppImage = image.player(opponent.id);
  if (oppImage) {
    const testX = oppImage.position.x + oppImage.movement.x * 0.15 + 4 * team.side;
    if (testX * team.side > deepest * team.side) {
      deepest = oppImage.position.x + oppImage.movement.x * 0.1 + 4 * team.side;
    }
  }
  const ownOffsideLine = offsideLine(match, team.id, image, 0);
  if (ownOffsideLine * team.side - 4 > deepest * team.side) {
    deepest = ownOffsideLine - 4 * team.side;
  }
  return deepest;
}

/** Second-last defender/ball line used both by the block and attacking support. */
export function offsideLine(
  match: Match,
  defendingTeamId: number,
  image: MentalImage,
  futureMs: number,
): number {
  const team = match.team(defendingTeamId);
  const xs = team.players.map((p) => {
    const pi = image.player(p.id);
    return (pi?.position.x ?? p.position.x) + (pi?.movement.x ?? p.movement.x) * futureMs * 0.001;
  });
  xs.sort((a, b) => b * team.side - a * team.side);
  // The original seeds the second-deepest search at the halfway line. If the whole
  // defence has crossed into the opposition half, attackers still cannot be offside
  // in their own half.
  let line = 0;
  for (const x of xs.slice(1)) {
    if (x * team.side > line * team.side) line = x;
  }
  const ballX = image.ballPrediction(0, _probeB).x;
  if (ballX * team.side > line * team.side) line = ballX;
  if (line * team.side < 0) line = 0.01 * -team.side;
  return clamp(line, -PITCH.halfLength, PITCH.halfLength);
}

export function applyOffsideTrap(position: Vector3, team: TeamState): Vector3 {
  const half = 2;
  const absolute = position.x * team.side;
  const trap = team.offsideTrapX * team.side;
  if (absolute > trap - half) {
    const front = trap - half;
    const factor = clamp((absolute - front) / (half * 2), 0, 1);
    position.x = (front + half * factor) * team.side;
  }
  return position;
}

function calculateDynamicRoles(match: Match, team: TeamState): void {
  const players = team.players.filter((p) => p.role !== 'GK');
  if (players.length === 0) return;
  const targets = players.map((p) => ({
    entry: p.formationEntry,
    position: adaptedFormationPosition(match, p, false),
  }));
  const costs = players.map((p) =>
    targets.map((target) =>
      Math.round(p.position.clone().addScaledVector(p.movement, 0.5).distanceTo(target.position) * 10),
    ),
  );
  const assignment = minimumAssignment(costs, Infinity);
  if (!assignment) return;
  players.forEach((player, i) => {
    player.dynamicFormationEntry = cloneEntry(targets[assignment[i]!]!.entry);
  });
}

/**
 * Bitmask DP, exact for the ten outfield slots and much smaller than a generic solver — but
 * only exact for whatever `cutoff` it is given. Always call it with `Infinity`: a finite cutoff
 * excludes edges outright, and the true minimum-cost assignment can require one of them. Kept
 * as a parameter rather than hard-coded so a test can pin the difference against a real cutoff.
 */
export function minimumAssignment(costs: number[][], cutoff: number): number[] | null {
  const n = costs.length;
  const states = 1 << n;
  const dp = new Float64Array(states);
  dp.fill(Infinity);
  dp[0] = 0;
  const previous = new Int16Array(states);
  previous.fill(-1);
  for (let mask = 0; mask < states; mask++) {
    const row = bitCount(mask);
    if (row >= n || !Number.isFinite(dp[mask]!)) continue;
    for (let col = 0; col < n; col++) {
      if (mask & (1 << col)) continue;
      const cost = costs[row]![col]!;
      if (cost >= cutoff) continue;
      const next = mask | (1 << col);
      if (dp[mask]! + cost < dp[next]!) {
        dp[next] = dp[mask]! + cost;
        previous[next] = col;
      }
    }
  }
  let mask = states - 1;
  if (!Number.isFinite(dp[mask]!)) return null;
  const result = new Array<number>(n);
  for (let row = n - 1; row >= 0; row--) {
    const col = previous[mask]!;
    if (col < 0) return null;
    result[row] = col;
    mask ^= 1 << col;
  }
  return result;
}

function bitCount(value: number): number {
  let result = 0;
  for (let n = value; n !== 0; n &= n - 1) result++;
  return result;
}

/**
 * How much of a lead a challenger needs over the defender already marking a man before the
 * mark changes hands, in `markingQuality`'s own units (0 to 1).
 *
 * MEASURED, not guessed: sampled every live recompute over a played-out match (410 of them,
 * 1,230 opponent-slot evaluations), the best and second-best candidate for a slot are within
 * 0.05 of each other 40% of the time and within 0.10 of each other 57% of the time — this is
 * a near-tie far more often than not. With no margin at all the winning defender for a given
 * opponent changed identity on 248 of those 1,230 evaluations, one in five, because the whole
 * assignment is redrawn from nothing every 400 ms and a near-tie is decided by sub-metre
 * position noise. 0.05 sits at that 40th percentile: it absorbs the near-ties driving most of
 * the churn while still letting a genuinely better-placed defender take over. Below this
 * margin a mark could still swap on real movement, which is the point — only the coin-flip
 * swaps are being removed. Same idea as `updateDesignatedPlayer`'s "somewhat better, to
 * overcome possession-chaos" margin for the ball-chaser, which this never had.
 */
export const MARKING_INCUMBENT_MARGIN = 0.05;

function calculateManMarking(match: Match, team: TeamState, image: MentalImage): void {
  // Who was marking whom before the reset below, so a defender who already has his man can
  // keep him against a near-tied challenger. Every mark is still reconsidered every 400 ms —
  // this only changes what it takes to actually switch it.
  const incumbentOf = new Map<number, number>(); // opponent id -> marking player id
  for (const player of team.players) {
    if (player.manMarkingId !== -1) incumbentOf.set(player.manMarkingId, player.id);
    player.manMarkingId = -1;
  }
  const dangerOrigin = new Vector3((PITCH.halfLength - 2) * team.side, 0, 0)
    .multiplyScalar(0.8)
    .addScaledVector(match.ball.predict(100, _probeA).setZ(0), 0.2);
  const opponents = match
    .opponentTeam(team.id)
    .players.map((opponent) => {
      const pi = image.player(opponent.id);
      let danger =
        1 - normalizedClamp((pi?.position ?? opponent.position).distanceTo(dangerOrigin), 0, 110);
      danger *= 0.95;
      if (opponent === match.opponentTeam(team.id).designatedPlayer) danger += 0.05;
      return { opponent, danger };
    })
    .sort((a, b) => b.danger - a.danger)
    .slice(0, 3);
  const available = team.players.filter((p) => p.role !== 'GK');
  for (const { opponent } of opponents) {
    let bestIndex = -1;
    let bestQuality = -1;
    const incumbentId = incumbentOf.get(opponent.id);
    available.forEach((player, i) => {
      let quality = markingQuality(player, opponent, team.side, image);
      if (player.id === incumbentId) quality += MARKING_INCUMBENT_MARGIN;
      if (quality > bestQuality) {
        bestQuality = quality;
        bestIndex = i;
      }
    });
    if (bestIndex >= 0) available.splice(bestIndex, 1)[0]!.manMarkingId = opponent.id;
  }
}

export function markingQuality(
  player: MatchPlayer,
  opponent: MatchPlayer,
  side: -1 | 1,
  image: MentalImage,
): number {
  const p = image.player(player.id);
  const o = image.player(opponent.id);
  const playerPos = (p?.position ?? player.position).clone().addScaledVector(p?.movement ?? player.movement, 0.1);
  const oppPos = (o?.position ?? opponent.position).clone().addScaledVector(o?.movement ?? opponent.movement, 0.1);
  const toGoal = new Vector3(PITCH.halfLength * side, 0, 0).sub(playerPos);
  const n = normalizedOr(toGoal, FORWARD);
  const length = clamp(toGoal.length(), 4, 14);
  const center = playerPos.clone().addScaledVector(n, -0.5);
  const perpendicular = new Vector3(-n.y, n.x, 0);
  const a = center.clone().addScaledVector(perpendicular, -length);
  const line = perpendicular.clone().multiplyScalar(length * 2);
  // Deliberately unclamped. `Line::GetDistanceToPoint` takes the projection straight from
  // `GetClosestToPoint`, which never clamps it, and then measures to the foot of the
  // perpendicular on the INFINITE line through the two vertices (`line.cpp:61-81`). So an
  // opponent standing off the ends of a defender's marking line is judged purely on how far he
  // is from that line, and being off the ends is charged separately, once, just below
  // (`teamAIcontroller.cpp:552-556`). Clamping here would fold the along-the-line displacement
  // into the distance as well and charge him for it twice, which hands the man-mark to a
  // different defender in roughly one arrangement of players in six.
  const u = oppPos.clone().sub(a).dot(line) / line.lengthSq();
  const closest = a.clone().addScaledVector(line, u);
  const distance = oppPos.distanceTo(closest);
  const oppIsGoalSide = Math.sign(line.x * (oppPos.y - a.y) - line.y * (oppPos.x - a.x)) >= 0;
  const adapted = oppIsGoalSide ? Math.abs(distance - 2) : distance;
  let result =
    1 - Math.pow(normalizedClamp(adapted, 0, 60), 0.5) * 0.5 -
    Math.pow(clamp(Math.abs(u * 2 - 1), 0, 1), 0.5) * 0.5;
  result = clamp(result, 0, 1);
  if (!oppIsGoalSide) result *= 0.6;
  const proximity = 1 - normalizedClamp(playerPos.distanceTo(oppPos), 0, 110);
  return result * 0.8 + proximity * 0.2;
}

function updateAttackingRun(match: Match, team: TeamState, image: MentalImage): void {
  if (team.attackingRunUntilMs > match.timeMs || match.bestPossessionTeamId !== team.id) return;
  // This side's own man on the ball, not the match's. `SelectAttackingRunPlayer` reads
  // `team->GetDesignatedTeamPossessionPlayer()` (`teamAIcontroller.cpp:90-97`), and so does the
  // distance rating below (`:212`). The match-wide focus changes sides only reluctantly, so on
  // the tick this side wins the race to the ball it can still be an opponent — which would
  // measure the run from the wrong man and, because he is who gets excluded, leave this side's
  // own ball-chaser free to be sent on the run instead. `updateForwardSupport` below already
  // reads the team's own (`:240-242`).
  const owner = team.designatedPlayer;
  const target = owner.position.clone().add(new Vector3(-team.side * 26, 0, 0));
  const candidates = team.players.filter((p) => p !== owner && p.role !== 'GK');
  candidates.sort((a, b) => a.position.distanceTo(target) - b.position.distanceTo(target));
  const runner = candidates[0];
  if (!runner) return;
  const distanceRating = Math.pow(1 - normalizedClamp(runner.position.distanceTo(owner.position), 0, 40), 0.5);
  const spot = new Vector3(runner.position.x + team.side * 10, runner.position.y * 0.8, 0);
  const opponents = match.opponentTeam(team.id).players
    .map((p) => image.player(p.id)?.position ?? p.position)
    .sort((a, b) => a.distanceTo(spot) - b.distanceTo(spot))
    .slice(0, 4);
  let density = 1;
  for (const position of opponents) {
    density -= Math.pow(curve(1 - normalizedClamp(position.distanceTo(spot), 0, 15), 1), 0.5) * 0.3;
  }
  if (distanceRating * density >= 0.5) {
    team.attackingRunPlayer = runner;
    team.attackingRunUntilMs = match.timeMs + 4000;
  }
}

function updateForwardSupport(team: TeamState): void {
  const owner = team.designatedPlayer;
  const target = owner.position.clone().add(new Vector3(-team.side * 1.5, 0, 0));
  team.forwardSupportPlayer =
    team.players
      .filter((p) => p !== owner && p.role !== 'GK')
      .sort((a, b) => a.position.distanceTo(target) - b.position.distanceTo(target))[0] ?? null;
}

function cloneEntry(entry: FormationEntry): FormationEntry {
  return {
    role: entry.role,
    databasePosition: entry.databasePosition.clone(),
    position: entry.position.clone(),
  };
}

const _avgA = new Vector3();
const _avgB = new Vector3();
const _probeA = new Vector3();
const _probeB = new Vector3();
